import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AppConfigService } from '@app/core/services/app-config.service';
import { CassError } from '@core/models/cass-error';
import { AuthService } from '@core/services/auth.service';
import { Logger } from '@core/services/logger.service';
import { TranslateService } from '@ngx-translate/core';
import { IndeterminateHorizontalProgressIndicatorComponent } from '@shared/components/indeterminate-horizontal-progress-indicator/indeterminate-horizontal-progress-indicator.component';
import { BehaviorSubject, distinctUntilChanged } from 'rxjs';

@Component({
	selector: 'cass-app-login',
	templateUrl: './login.component.html',
	styleUrls: ['./login.component.css']
})
export class LoginComponent implements OnInit {

	private readonly COMPONENT_NAME = "LoginComponent";

	@ViewChild("emailFormField", { read: ElementRef }) emailInputRef!: ElementRef<HTMLInputElement>;
	@ViewChild("passwordFormField", { read: ElementRef }) passwordInputRef!: ElementRef<HTMLInputElement>

	formGroup!: FormGroup;
	email!: FormControl;
	password!: FormControl;
	loginErrorMessage: string = "";

	readonly loggingIn$ = new BehaviorSubject<boolean>(false);
	private fullscreenProgressOverlayRef?: OverlayRef;

	protected isPasswordShown = false;

	constructor (
		public appConfigService: AppConfigService,
		private _logger: Logger,
		private _authService: AuthService,
		private _router: Router,
		private _translate: TranslateService,
		private _overlay: Overlay) {
		this.email = new FormControl('', [Validators.required, Validators.email]);
		this.password = new FormControl('', Validators.required);

		this.formGroup = new FormGroup({
			email: this.email,
			password: this.password
		});
	}

	ngOnInit(): void {
		this.formGroup.valueChanges.subscribe(() => {
			if (this.loginErrorMessage) {
				this.loginErrorMessage = "";
			}
		});

		this.fullscreenProgressOverlayRef = this._overlay.create({
			positionStrategy: this._overlay.position().global().centerHorizontally().centerVertically(),
			hasBackdrop: true
		});

		this.loggingIn$.pipe(distinctUntilChanged()).subscribe(show => {
			if (show) {
				const componentRef = this.fullscreenProgressOverlayRef?.attach(new ComponentPortal(IndeterminateHorizontalProgressIndicatorComponent));
				if (componentRef) {
					this._translate.get("LOGIN.LOGGING_IN_NOTIFICATION").subscribe((msg) => componentRef.instance.displayString = msg);
				}
			}
			else {
				this.fullscreenProgressOverlayRef?.detach();
			}
		});

		setTimeout(() => {
			const emailValue = this.emailInputRef?.nativeElement.value;
			if (emailValue) {
				this.formGroup.controls["email"].setValue(emailValue);
			}

			const passwordlValue = this.passwordInputRef?.nativeElement.value;
			if (passwordlValue) {
				this.formGroup.controls["password"].setValue(passwordlValue);
			}
		}, 1000);
	}

	login(): void {
		if (!this.formGroup.valid) {
			// trigger all field validation messages
			this.formGroup.markAllAsTouched();
		}
		else {
			// login attempt
			this.loggingIn$.next(true);
			this._authService.login(this.email.value, this.password.value).subscribe({
				next: () => {
					this.loggingIn$.next(false);
					this._logger.info(this.COMPONENT_NAME, `Login succeeded for email="${this.email.value}"`);
					this._router.navigate(['/dashboard']);
				},
				error: (error: CassError) => {
					this.loggingIn$.next(false);
					this._logger.error(this.COMPONENT_NAME, `Login failed for email="${this.email.value}"`, error);

					if (error.httpStatus === 401 && error.errorCode === 1) {
						this._translate.get("ERRORS.LOGIN_FAILED").subscribe((val: string) => this.loginErrorMessage = val);
					}
					else if (error.errorMessageKey) {
						this._translate.get(error.errorMessageKey, error.errorInterpolateParams).subscribe((val) => this.loginErrorMessage = val);
					}
					else if (error.errorMessage) {
						this._translate.get("LOGIN.LOGIN_FAILED_MSG_TTILE").subscribe((val: string) => this.loginErrorMessage = `${val}: ${error.errorMessage}`)
					}
					else {
						this._translate.get("ERRORS.UNKNOWN_ERROR").subscribe((val) => this.loginErrorMessage = val);
					}
				}
			});
		}
	}
}
